import { Suspense, useEffect, useRef, useState } from "react";
import { Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import { ChevronDown, ChevronRight, LogOut } from "lucide-react";
import { logout, useMe } from "../../api/hooks/useAuth";
import { Button } from "../ui/button";
import { Loading } from "../atoms";
import { BrandLockup } from "../ui/logo";
import { CONSIGNOR_PATHS, MENU, allowsPath, groupOf, menuFor } from "../../lib/menu";
import { ICONS } from "../../lib/icons";

const OPEN_KEY = "csms.nav.open";



/**
 * 인증된 콘솔의 사이드바 + 상단바 + 본문 템플릿. 자식 라우트는 Outlet 에.
 *
 * 사이드바는 `lib/menu.ts` 의 정본 트리를 그린다(기존 화면 정의 메뉴 트리 + 2025 개편 시나리오).
 * 주메뉴를 누르면 보조메뉴가 접히고 펴진다 — 서른 개가 한 화면에 다 펼쳐져 있으면
 * 지금 어디 있는지가 안 보인다.
 *
 * 접힘 상태는 두 가지가 함께 정한다.
 *   지금 있는 화면의 주메뉴는 **항상 펴 둔다.** 접힌 채로 두면 현재 위치가 사라진다.
 *   나머지는 사용자가 접은 대로 기억한다(localStorage). 새로고침마다 다시 접는 일이 없다.
 *
 * 아직 화면이 없는 항목도 흐리게 그린다. 목록에서 빼면 무엇이 남았는지 안 보이고,
 * 링크로 두면 눌러서 빈 화면을 본다.
 */
export default function ConsoleLayout() {
  const { data: me } = useMe();
  const { pathname } = useLocation();
  const sideRef = useRef<HTMLElement>(null);
  const active = groupOf(pathname);

  const [open, setOpen] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(OPEN_KEY);
      if (raw) return JSON.parse(raw) as string[];
    } catch {
      // 저장이 막힌 브라우저(사생활 모드 등). 기본값으로 간다.
    }
    // 처음 열면 화면이 붙어 있는 주메뉴만 펴 둔다. 전부 펴면 서른 줄이 쏟아진다.
    return MENU.filter((g) => g.items.some((i) => i.to)).map((g) => g.label);
  });

  useEffect(() => {
    try {
      localStorage.setItem(OPEN_KEY, JSON.stringify(open));
    } catch {
      // 못 저장해도 화면은 그대로 돈다.
    }
  }, [open]);

  const toggle = (label: string) =>
    setOpen((prev) => (prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]));

  // 지금 보고 있는 메뉴가 사이드바 밖에 있으면 끌어온다 — 화면을 옮길 때마다 어디에
  // 있는지 찾으려 매번 손으로 내려야 했다(사용자 지적 2026-09-16).
  useEffect(() => {
    const el = sideRef.current?.querySelector('[aria-current="page"]');
    // jsdom 에는 scrollIntoView 가 없다. 화면에서만 하는 일이라 없으면 그냥 넘어간다.
    el?.scrollIntoView?.({ block: "nearest" });
  }, [pathname, open, active]);

  if (me && !allowsPath(me.role, pathname)) return <Navigate to={CONSIGNOR_PATHS[0]} replace />;
  const menu = menuFor(me?.role);

  return (
    // 사이드바·상단바는 흰 종이, 본문은 연한 회색이다. 셋 다 흰색이면 어디까지가 메뉴이고
    // 어디부터가 내용인지 눈이 못 가른다(2026-08-31 사용자 지적). 본문 카드가 다시 흰색이라
    // 회색 바탕 위에서 표와 패널이 떠 보인다.
    <div className="grid min-h-screen grid-cols-[232px_1fr] bg-canvas">
      {/* 사이드바는 화면에 붙어 있고 스스로 스크롤한다. 본문과 함께 흘러가면 긴 목록을
          내릴 때 메뉴가 위로 사라져, 보고 있는 메뉴를 끌어와도 소용이 없다
          (사용자 지적 2026-09-16). 상단바가 sticky 라 높이는 화면 한 판으로 잡는다. */}
      <aside ref={sideRef}
        className="sticky top-0 flex h-screen flex-col overflow-y-auto border-r bg-background px-3 py-4">
        <div className="mb-4 px-2 py-1">
          <BrandLockup />
        </div>
        <nav className="flex flex-col gap-3">
          {menu.map((group) => {
            // 지금 보고 있는 화면이 든 주메뉴는 접어 두지 않는다.
            const expanded = open.includes(group.label) || group.label === active;
            return (
              <div key={group.label} className="flex flex-col gap-0.5">
                <button
                  type="button"
                  // 주메뉴가 보조메뉴보다 커야 한다. 반대면 어느 쪽이 묶음인지 눈이 헷갈린다
                  // (2026-08-31 사용자 지적 — 1depth 가 2depth 보다 작았다).
                  className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-[14px] font-semibold text-foreground hover:bg-muted"
                  aria-expanded={expanded}
                  onClick={() => toggle(group.label)}
                >
                  {(() => {
                    const Icon = ICONS[group.icon];
                    return Icon ? <Icon size={16} className="shrink-0 text-muted-foreground" /> : null;
                  })()}
                  <span className="flex-1 text-left">{group.label}</span>
                  {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
                {expanded &&
                  group.items.map((item) =>
                    item.to ? (
                      <NavLink
                        key={item.label}
                        to={item.to}
                        // 메뉴로 들어오면 목록 첫 화면 — 상세에서 「목록으로」 올 때만 보던 쪽을 되살린다(useListPage).
                        state={{ fresh: true }}
                        end={item.to === "/"}
                        className={({ isActive }) =>
                          [
                            // 보조메뉴는 주메뉴보다 작고 한 칸 들어가 있어야 묶음이 보인다.
                            // antialiased: 작은 13px 글씨가 서브픽셀 렌더로 흐릿해 보이던 것 방지.
                            "rounded-md py-1.5 pl-8 pr-2.5 text-[13px] antialiased",
                            isActive
                              ? "bg-primary/10 font-semibold text-primary"
                              // 기본색을 muted 보다 진하게(foreground/70) — 오버 전에도 또렷하게.
                              // muted-foreground 는 옅어 작은 글씨가 「깨진 듯」 보였다(2026-09-28 사용자 보고).
                              : "text-foreground/70 hover:bg-muted hover:text-foreground",
                          ].join(" ")
                        }
                      >
                        {item.label}
                      </NavLink>
                    ) : (
                      // 아직 화면이 없는 것. 자리는 보이되 눌리지 않는다.
                      <span
                        key={item.label}
                        className="flex cursor-default items-center gap-1.5 py-1.5 pl-8 pr-2.5 text-[13px] text-muted-foreground/50"
                        title={item.note ? `준비 중 — ${item.note}` : "준비 중"}
                      >
                        {item.label}
                        {/* 준비 중이라는 것을 흐린 색만으로 알리면 색약인 사람에게 안 보인다. */}
                        <span className="rounded border px-1 text-[10px] leading-4">준비 중</span>
                      </span>
                    ),
                  )}
              </div>
            );
          })}
        </nav>
      </aside>
      <div className="flex min-w-0 flex-col">
        <header className="sticky top-0 z-10 flex items-center justify-between border-b bg-background px-6 py-2.5 text-sm shadow-sm">
          <span className="text-muted-foreground">
            {me?.co_nm ?? ""} <span className="font-medium text-foreground">{me?.manager_nm ?? me?.lgn_id ?? ""}</span>
          </span>
          <Button variant="ghost" size="sm" onClick={logout}>
            <LogOut size={15} />
            로그아웃
          </Button>
        </header>
        <main className="min-w-0 flex-1 bg-canvas p-6">
          <Suspense fallback={<Loading />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    </div>
  );
}
